import type { Stroke, Point } from '../../types';
import type { HandlePosition } from '../diagram/state/types';
import type { DiagramConnectorManager } from '../diagram/controllers/DiagramConnectorManager';
import { Shape } from '../diagram/shapes/Shape';
import { Connector } from '../diagram/shapes/Connector';
import { TextElement } from '../diagram/shapes/TextElement';
import { DrawShape } from '../draw/shapes';
import { resizeStroke } from '../draw/strokeResize';
import type { UnifiedStore } from './UnifiedStore';
import type { UnifiedSelectionManager } from './UnifiedSelectionManager';

interface UnifiedDragContext {
  store: UnifiedStore;
  ctx: CanvasRenderingContext2D;
  snapEnabled: boolean;
  snapToGrid: (point: Point) => Point;
  connectorManager: DiagramConnectorManager;
  selectionManager: UnifiedSelectionManager;
  render: () => void;
  notifyChange: () => void;
}

type DragTarget = Shape | Connector | TextElement | DrawShape | Stroke;
const DRAG_THRESHOLD = 3;

export class UnifiedDragController {
  private readonly context: UnifiedDragContext;
  private dragStart: Point | null = null;
  private dragOrigin: Point | null = null;
  private dragTarget: DragTarget | null = null;
  private hasDragChanges = false;
  private resizeHandle: HandlePosition | null = null;

  constructor(context: UnifiedDragContext) {
    this.context = context;
  }

  isActive(): boolean {
    return this.dragTarget !== null && this.dragStart !== null;
  }

  startDrag(point: Point, target: DragTarget | null, handle: HandlePosition | null): void {
    this.dragStart = point;
    this.dragOrigin = point;
    this.dragTarget = target;
    this.resizeHandle = handle;
    this.hasDragChanges = false;
    if (handle && target instanceof TextElement) {
      target.startResize(this.context.ctx);
    }
  }

  updateDrag(point: Point): void {
    if (!this.dragTarget || !this.dragStart) return;

    const { snapEnabled, snapToGrid } = this.context;
    const effectivePoint = this.resizeHandle && snapEnabled ? snapToGrid(point) : point;
    const effectiveStart = this.resizeHandle && snapEnabled ? snapToGrid(this.dragStart) : this.dragStart;

    const delta = {
      x: effectivePoint.x - effectiveStart.x,
      y: effectivePoint.y - effectiveStart.y,
    };

    if (!this.hasDragChanges && this.dragOrigin) {
      const totalDx = effectivePoint.x - this.dragOrigin.x;
      const totalDy = effectivePoint.y - this.dragOrigin.y;
      if (Math.abs(totalDx) >= DRAG_THRESHOLD || Math.abs(totalDy) >= DRAG_THRESHOLD) {
        this.hasDragChanges = true;
      } else {
        return;
      }
    }

    if (this.resizeHandle) {
      this.handleResize(effectivePoint, delta);
    } else {
      this.handleMove(delta);
    }

    this.dragStart = effectivePoint;
    this.context.render();
  }

  private handleResize(effectivePoint: Point, delta: Point): void {
    if (!this.dragTarget || !this.resizeHandle) return;
    const { connectorManager, ctx } = this.context;

    if (this.dragTarget instanceof TextElement) {
      this.dragTarget.resize(this.resizeHandle, delta, ctx);
    } else if (this.dragTarget instanceof Shape) {
      this.dragTarget.resize(this.resizeHandle, delta);
      connectorManager.updateConnectedConnectors(this.dragTarget);
    } else if (this.dragTarget instanceof Connector) {
      this.resizeConnector(this.dragTarget, effectivePoint);
    } else if (this.dragTarget instanceof DrawShape) {
      this.dragTarget.resize(this.resizeHandle, effectivePoint);
    } else if (this.isStroke(this.dragTarget)) {
      resizeStroke(this.dragTarget, this.resizeHandle, effectivePoint);
    }
  }

  private resizeConnector(connector: Connector, effectivePoint: Point): void {
    const { connectorManager } = this.context;

    if (this.resizeHandle === 'top-left') {
      const snapInfo = connectorManager.findNearestConnectionPoint(effectivePoint);
      if (snapInfo) {
        connector.startPoint = { ...snapInfo.position };
        connector.startShapeId = snapInfo.shapeId;
        connector.startConnectionPointId = snapInfo.connectionPointId;
      } else {
        connector.startPoint = { ...effectivePoint };
        connector.startShapeId = null;
        connector.startConnectionPointId = null;
      }
    } else if (this.resizeHandle === 'bottom-right') {
      const snapInfo = connectorManager.findNearestConnectionPoint(effectivePoint);
      if (snapInfo) {
        connector.endPoint = { ...snapInfo.position };
        connector.endShapeId = snapInfo.shapeId;
        connector.endConnectionPointId = snapInfo.connectionPointId;
      } else {
        connector.endPoint = { ...effectivePoint };
        connector.endShapeId = null;
        connector.endConnectionPointId = null;
      }
    } else if (this.resizeHandle === 'top') {
      if (connector.arcPoint) {
        connector.arcPoint = { ...effectivePoint };
      }
    } else if (this.resizeHandle?.startsWith('waypoint-')) {
      const index = parseInt(this.resizeHandle.split('-')[1]!, 10);
      if (connector.waypoints[index]) {
        const delta = {
          x: effectivePoint.x - this.dragStart!.x,
          y: effectivePoint.y - this.dragStart!.y,
        };
        connector.waypoints[index]!.x += delta.x;
        connector.waypoints[index]!.y += delta.y;
      }
    }
  }

  private handleMove(delta: Point): void {
    if (!this.dragTarget) return;

    const { store, connectorManager } = this.context;
    const selectedIds = store.selectedElements;

    // Multi-selection move
    if (selectedIds.has(this.dragTarget.id) && selectedIds.size > 1) {
      const selectedConnectorIds = new Set(
        store.connectors.filter(c => selectedIds.has(c.id)).map(c => c.id)
      );

      for (const shape of store.shapes) {
        if (!selectedIds.has(shape.id)) continue;
        shape.move(delta);
        connectorManager.updateConnectedConnectors(shape, selectedConnectorIds);
      }
      for (const connector of store.connectors) {
        if (!selectedIds.has(connector.id)) continue;
        connector.move(delta);
      }
      for (const textElement of store.textElements) {
        if (!selectedIds.has(textElement.id)) continue;
        textElement.move(delta);
      }
      for (const drawShape of store.drawShapes) {
        if (!selectedIds.has(drawShape.id)) continue;
        drawShape.move(delta);
      }
      for (const stroke of store.strokes) {
        if (!selectedIds.has(stroke.id)) continue;
        for (const p of stroke.points) {
          p.x += delta.x;
          p.y += delta.y;
        }
      }
    } else {
      // Single element move
      if (this.isStroke(this.dragTarget)) {
        for (const p of this.dragTarget.points) {
          p.x += delta.x;
          p.y += delta.y;
        }
      } else {
        this.dragTarget.move(delta);
        if (this.dragTarget instanceof Shape) {
          connectorManager.updateConnectedConnectors(this.dragTarget);
        }
      }
    }
  }

  finishDrag(): boolean {
    if (!this.dragTarget) return false;

    const { store, snapEnabled, snapToGrid, connectorManager, notifyChange } = this.context;
    const selectedIds = store.selectedElements;
    const hadChanges = this.hasDragChanges;

    // Apply final snapping
    if (selectedIds.has(this.dragTarget.id) && selectedIds.size > 1) {
      if (snapEnabled) {
        for (const shape of store.shapes) {
          if (!selectedIds.has(shape.id)) continue;
          const snapped = snapToGrid(shape.position);
          shape.position.x = snapped.x;
          shape.position.y = snapped.y;
          connectorManager.updateConnectedConnectors(shape);
          connectorManager.tryAutoConnectShape(shape);
        }
        for (const connector of store.connectors) {
          if (!selectedIds.has(connector.id)) continue;
          connectorManager.snapAndUpdateConnectorEndpoints(connector);
        }
        for (const textElement of store.textElements) {
          if (!selectedIds.has(textElement.id)) continue;
          const snapped = snapToGrid(textElement.position);
          textElement.position.x = snapped.x;
          textElement.position.y = snapped.y;
        }
        for (const drawShape of store.drawShapes) {
          if (!selectedIds.has(drawShape.id)) continue;
          const snapped = snapToGrid(drawShape.position);
          drawShape.position.x = snapped.x;
          drawShape.position.y = snapped.y;
        }
      }
    } else if (this.dragTarget instanceof DrawShape && snapEnabled && hadChanges) {
      const snapped = snapToGrid(this.dragTarget.position);
      this.dragTarget.position.x = snapped.x;
      this.dragTarget.position.y = snapped.y;
    } else if (this.dragTarget instanceof TextElement && snapEnabled && hadChanges) {
      const snapped = snapToGrid(this.dragTarget.position);
      this.dragTarget.position.x = snapped.x;
      this.dragTarget.position.y = snapped.y;
    } else if (this.dragTarget instanceof Shape && snapEnabled) {
      const snapped = snapToGrid(this.dragTarget.position);
      this.dragTarget.position.x = snapped.x;
      this.dragTarget.position.y = snapped.y;
      connectorManager.updateConnectedConnectors(this.dragTarget);
      connectorManager.tryAutoConnectShape(this.dragTarget);
    } else if (this.dragTarget instanceof Connector) {
      connectorManager.snapAndUpdateConnectorEndpoints(this.dragTarget);
    }

    if (hadChanges) {
      notifyChange();
    }

    this.reset();
    return hadChanges;
  }

  cancelDrag(): void {
    this.reset();
  }

  private reset(): void {
    if (this.dragTarget instanceof TextElement && this.resizeHandle) {
      this.dragTarget.endResize();
    }
    this.dragTarget = null;
    this.dragStart = null;
    this.dragOrigin = null;
    this.resizeHandle = null;
    this.hasDragChanges = false;
  }

  private isStroke(target: DragTarget): target is Stroke {
    return 'points' in target && 'baseWidth' in target && 'tool' in target;
  }
}
